import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3, CheckCircle2, RefreshCw, RotateCcw, Star, Users,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import { certificacionesApi, apiErrorMessage } from '../../services/certificacionesApi';
import { ACCENT, BORDER, CARD, colorForFabricante, selectCls, tint } from './ui';

const COLORS = ['#00B8FF', '#8644FF', '#00D68F', '#F59E0B', '#F43F8E', '#7DD3FC', '#A78BFA', '#94A3B8'];
const pct = value => `${(Number(value || 0) * 100).toFixed(1)}%`;
const norm = value => String(value || '').trim();
const unique = (rows, key) => [...new Set(rows.map(r => norm(r[key])).filter(Boolean))].sort();

// Mismos umbrales que la hoja Criterios Score del sheet migrado.
const CALIFICACION_LEGEND = [
  { rango: '90% - 120%', filled: 5, desc: 'Supera ampliamente las expectativas. Demuestra excelencia, compromiso y dominio total de los contenidos.' },
  { rango: '80% - 89%', filled: 4, desc: 'Cumple consistentemente con los objetivos. Muestra buen desempeño y aplicación práctica del aprendizaje.' },
  { rango: '70% - 79%', filled: 3, desc: 'Alcanza los resultados mínimos esperados. Tiene áreas de mejora pero mantiene un nivel funcional.' },
  { rango: '60% - 69%', filled: 2, desc: 'Cumplimiento limitado o irregular. Requiere apoyo adicional y seguimiento para mejorar resultados.' },
  { rango: '-60%', filled: 1, desc: 'No cumple con los estándares establecidos. Necesita intervención inmediata o repetir el proceso formativo.' },
];

function starsFor(value) {
  const v = Number(value || 0);
  const filled = v >= .9 ? 5 : v >= .8 ? 4 : v >= .7 ? 3 : v >= .6 ? 2 : 1;
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={12} fill={i < filled ? '#f59e0b' : 'none'} stroke="#f59e0b" />
      ))}
    </span>
  );
}

function countBy(rows, key) {
  const counts = new Map();
  rows.forEach(row => {
    const value = norm(row[key]) || 'Sin dato';
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

function ResetBar({ filtrosActivos, onReset }) {
  return (
    <div className="flex justify-end">
      <button onClick={onReset} disabled={!filtrosActivos}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all disabled:opacity-30"
        style={{ borderColor: filtrosActivos ? BORDER : 'rgba(255,255,255,.1)', color: filtrosActivos ? ACCENT : '#fff' }}>
        <RotateCcw size={13} /> Restablecer todo
      </button>
    </div>
  );
}

function Kpi({ label, value, tone = '#fff', icon: Icon }) {
  return (
    <div className={`${CARD} p-4 min-h-[86px] flex items-center justify-between`}>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
        <p className="text-2xl font-bold mt-1" style={{ color: tone }}>{value}</p>
      </div>
      {Icon && <Icon size={20} style={{ color: tone, opacity: .7 }} />}
    </div>
  );
}

function VisualCard({ title, selected, onReset, className = '', children }) {
  return (
    <section className={`${CARD} p-4 ${className}`}>
      <div className="h-7 flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          {selected && <p className="text-[10px] mt-0.5" style={{ color: ACCENT }}>{selected}</p>}
        </div>
        <button onClick={onReset} disabled={!selected} title="Restablecer este filtro"
          className="p-1 rounded-md text-white/30 hover:text-sky-300 disabled:opacity-0 transition-all">
          <RotateCcw size={13} />
        </button>
      </div>
      {children}
    </section>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload || {};
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-xl border border-sky-400/20 bg-[#04141f] text-white">
      <div className="font-medium">{item.name}</div>
      <div style={{ color: ACCENT }}>{item.value}</div>
    </div>
  );
}

function Bars({ data, selected, onSelect, horizontal = false, colorFor }) {
  if (!data.length) return <Empty />;
  const height = Math.max(220, horizontal ? data.length * 30 : 220);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'} margin={{ top: 6, right: 8, left: horizontal ? 24 : 0, bottom: 8 }}>
        <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={!horizontal} horizontal={horizontal} />
        {horizontal ? (
          <>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={105} tick={{ fill: 'rgba(255,255,255,.55)', fontSize: 10 }} axisLine={false} tickLine={false} />
          </>
        ) : (
          <>
            <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,.5)', fontSize: 9 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
            <YAxis tick={{ fill: 'rgba(255,255,255,.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
          </>
        )}
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,184,255,.06)' }} />
        <Bar dataKey="value" radius={horizontal ? [0, 5, 5, 0] : [5, 5, 0, 0]} onClick={entry => onSelect(entry.name)}>
          {data.map((entry, i) => <Cell key={entry.name} fill={selected === entry.name ? '#fff' : (colorFor ? colorFor(entry, i) : COLORS[i % COLORS.length])} opacity={selected && selected !== entry.name ? .28 : 1} cursor="pointer" />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function Donut({ data, selected, onSelect, colorFor }) {
  if (!data.length) return <Empty />;
  return (
    <div className="grid grid-cols-[minmax(150px,1fr)_minmax(110px,.7fr)] items-center h-[220px]">
      <ResponsiveContainer width="100%" height={210}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}
            onClick={entry => onSelect(entry.name)}>
            {data.map((entry, i) => <Cell key={entry.name} fill={colorFor ? colorFor(entry, i) : COLORS[i % COLORS.length]} opacity={selected && selected !== entry.name ? .25 : 1} cursor="pointer" />)}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-2">
        {data.slice(0, 6).map((entry, i) => (
          <button key={entry.name} onClick={() => onSelect(entry.name)} className="w-full flex items-center gap-2 text-left">
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: colorFor ? colorFor(entry, i) : COLORS[i % COLORS.length], opacity: selected && selected !== entry.name ? .25 : 1 }} />
            <span className="text-[10px] text-white/55 truncate">{entry.name}</span>
            <span className="text-[10px] text-white/35 ml-auto">{entry.value}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Empty() {
  return <div className="h-[200px] flex items-center justify-center text-xs text-white/30">Sin datos</div>;
}

function MiniTable({ columns, rows, selected, keyField, onSelect, empty = 'Sin datos' }) {
  if (!rows.length) return <div className="h-[120px] flex items-center justify-center text-xs text-white/30 text-center px-4">{empty}</div>;
  return (
    <div className="overflow-auto max-h-[280px]">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[#082234] z-10">
          <tr>{columns.map(c => <th key={c.key} className="text-left py-2 px-2 font-medium text-white/40 uppercase text-[9px]">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const key = row[keyField];
            return (
              <tr key={`${key}-${i}`} onClick={() => onSelect(key)}
                className="border-b border-white/5 hover:bg-white/[.04] cursor-pointer"
                style={{ background: selected === key ? tint(.12) : undefined }}>
                {columns.map(c => <td key={c.key} className="py-2 px-2 text-white/65">{c.render ? c.render(row) : row[c.key] ?? '—'}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GlobalFilters({ fields, values, onChange, options }) {
  return (
    <div className={`${CARD} p-3 flex flex-wrap gap-2`}>
      {fields.map(field => (
        <select key={field.key} className={`${selectCls} min-w-[155px]`} value={values[field.key] || ''}
          onChange={e => onChange(field.key, e.target.value)}>
          <option value="">{field.all}</option>
          {(options[field.key] || []).map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
        </select>
      ))}
    </div>
  );
}

function CursosDashboard({ rows, loading }) {
  const [global, setGlobal] = useState({ equipo: '', correo: '', score: '', estatus: '' });
  const [cross, setCross] = useState({ fabricante: '', voucher: '', cumplio: '', estado: '', programa: '', colaborador: '', equipo: '' });
  const setC = (key, value) => setCross(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }));
  const reset = () => { setGlobal({ equipo: '', correo: '', score: '', estatus: '' }); setCross({ fabricante: '', voucher: '', cumplio: '', estado: '', programa: '', colaborador: '', equipo: '' }); };

  const filtered = useMemo(() => rows.filter(r => {
    const score = Number(r.total_pct_curso || 0);
    const scoreOk = !global.score || (global.score === '90' ? score >= .9 : global.score === '80' ? score >= .8 && score < .9 : global.score === '70' ? score >= .7 && score < .8 : score < .7);
    return (!global.equipo || r.equipo === global.equipo)
      && (!global.correo || r.correo === global.correo)
      && (!global.estatus || r.estatus === global.estatus)
      && scoreOk
      && (!cross.fabricante || r.fabricante === cross.fabricante)
      && (!cross.voucher || (r.tipo_voucher || 'N/A') === cross.voucher)
      && (!cross.cumplio || r.cumplio === cross.cumplio)
      && (!cross.estado || r.estatus === cross.estado)
      && (!cross.programa || r.programa === cross.programa)
      && (!cross.colaborador || r.correo === cross.colaborador)
      && (!cross.equipo || r.equipo === cross.equipo);
  }), [rows, global, cross]);

  const fabricantes = countBy(filtered, 'fabricante');
  const vouchers = countBy(filtered.map(r => ({ ...r, voucher: r.tipo_voucher || 'N/A' })), 'voucher');
  const cumplimiento = countBy(filtered, 'cumplio');
  const estados = countBy(filtered, 'estatus');
  const equipos = countBy(filtered, 'equipo').slice(0, 10);
  const personasFull = useMemo(() => {
    const map = new Map();
    filtered.forEach(r => {
      const item = map.get(r.correo) || { correo: r.correo, nombre: r.colaborador_nombre, equipo: r.equipo, total: 0, score: 0, cumplidos: 0, activos: 0 };
      item.total += 1; item.score += Number(r.total_pct_curso || 0); if (r.cumplio === 'Si') item.cumplidos += 1;
      if (r.estatus === 'En Proceso') item.activos += 1;
      map.set(r.correo, item);
    });
    return [...map.values()].map(x => ({ ...x, promedio: x.total ? x.score / x.total : 0 }));
  }, [filtered]);
  const personas = useMemo(
    () => [...personasFull].sort((a, b) => b.promedio - a.promedio).slice(0, 30),
    [personasFull]
  );
  const candidatos = useMemo(
    () => personasFull.filter(x => x.activos === 0).sort((a, b) => b.promedio - a.promedio).slice(0, 15),
    [personasFull]
  );
  const ranking = useMemo(
    () => [...personasFull].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [personasFull]
  );
  const programas = estado => {
    const map = new Map();
    filtered.filter(r => r.estatus === estado).forEach(r => {
      const item = map.get(r.programa) || { programa: r.programa, inscritos: 0, avance: 0 };
      item.inscritos += 1; item.avance += Number(r.porcentaje || 0); map.set(r.programa, item);
    });
    return [...map.values()].map(x => ({ ...x, avance: x.avance / x.inscritos })).sort((a, b) => b.inscritos - a.inscritos).slice(0, 15);
  };
  const activos = Object.values(global).some(Boolean) || Object.values(cross).some(Boolean);

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <ResetBar filtrosActivos={activos} onReset={reset} />
      <GlobalFilters
        fields={[
          { key: 'equipo', all: 'Todos los equipos' }, { key: 'correo', all: 'Todos los colaboradores' },
          { key: 'score', all: 'Todos los scores' }, { key: 'estatus', all: 'Todos los estatus' },
        ]}
        values={global} onChange={(k, v) => setGlobal(p => ({ ...p, [k]: v }))}
        options={{
          equipo: unique(rows, 'equipo'),
          correo: [...new Map(rows.map(r => [r.correo, { value: r.correo, label: r.colaborador_nombre }])).values()].sort((a, b) => (a.label || '').localeCompare(b.label || '')),
          score: [{ value: '90', label: '90% o más' }, { value: '80', label: '80% - 89%' }, { value: '70', label: '70% - 79%' }, { value: 'low', label: 'Menos de 70%' }],
          estatus: unique(rows, 'estatus'),
        }} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Registros" value={filtered.length} icon={BarChart3} />
        <Kpi label="Finalizados" value={filtered.filter(r => r.estatus === 'Finalizado').length} tone="#4ade80" icon={CheckCircle2} />
        <Kpi label="En proceso" value={filtered.filter(r => r.estatus === 'En Proceso').length} tone={ACCENT} icon={RefreshCw} />
        <Kpi label="Colaboradores" value={new Set(filtered.map(r => r.correo)).size} tone="#a78bfa" icon={Users} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <VisualCard title="Cursos por fabricante" selected={cross.fabricante} onReset={() => setC('fabricante', cross.fabricante)} className="xl:col-span-5">
          <Bars data={fabricantes} selected={cross.fabricante} onSelect={v => setC('fabricante', v)} colorFor={(e, i) => colorForFabricante(e.name, i)} />
        </VisualCard>
        <VisualCard title="Voucher" selected={cross.voucher} onReset={() => setC('voucher', cross.voucher)} className="xl:col-span-3">
          <Donut data={vouchers} selected={cross.voucher} onSelect={v => setC('voucher', v)} />
        </VisualCard>
        <VisualCard title="Cumplió" selected={cross.cumplio} onReset={() => setC('cumplio', cross.cumplio)} className="xl:col-span-4">
          <Donut data={cumplimiento} selected={cross.cumplio} onSelect={v => setC('cumplio', v)} />
        </VisualCard>
        <VisualCard title="Cursos por equipo" selected={cross.equipo} onReset={() => setC('equipo', cross.equipo)} className="xl:col-span-5">
          <Bars data={equipos} selected={cross.equipo} onSelect={v => setC('equipo', v)} horizontal />
        </VisualCard>
        <VisualCard title="Estatus" selected={cross.estado} onReset={() => setC('estado', cross.estado)} className="xl:col-span-3">
          <Donut data={estados} selected={cross.estado} onSelect={v => setC('estado', v)} />
        </VisualCard>
        <VisualCard title="Total de cursos asignados" selected={cross.colaborador} onReset={() => setC('colaborador', cross.colaborador)} className="xl:col-span-4">
          <MiniTable keyField="correo" selected={cross.colaborador} onSelect={v => setC('colaborador', v)} rows={personas}
            columns={[{ key: 'nombre', label: 'Colaborador' }, { key: 'equipo', label: 'Equipo' }, { key: 'promedio', label: 'Score', render: r => pct(r.promedio) }, { key: 'total', label: 'Asignaciones' }]} />
        </VisualCard>
        <VisualCard title="Cursos en proceso" selected={cross.programa} onReset={() => setC('programa', cross.programa)} className="xl:col-span-6">
          <MiniTable keyField="programa" selected={cross.programa} onSelect={v => setC('programa', v)} rows={programas('En Proceso')}
            columns={[{ key: 'programa', label: 'Programa' }, { key: 'avance', label: 'Avance', render: r => pct(r.avance) }, { key: 'inscritos', label: 'Personas' }]} />
        </VisualCard>
        <VisualCard title="Cursos finalizados" selected={cross.programa} onReset={() => setC('programa', cross.programa)} className="xl:col-span-6">
          <MiniTable keyField="programa" selected={cross.programa} onSelect={v => setC('programa', v)} rows={programas('Finalizado')}
            columns={[{ key: 'programa', label: 'Programa' }, { key: 'avance', label: 'Avance', render: r => pct(r.avance) }, { key: 'inscritos', label: 'Personas' }]} />
        </VisualCard>
        <VisualCard title="Candidatos para nuevos cursos" selected={cross.colaborador} onReset={() => setC('colaborador', cross.colaborador)} className="xl:col-span-6">
          <MiniTable keyField="correo" selected={cross.colaborador} onSelect={v => setC('colaborador', v)} rows={candidatos}
            empty="Sin candidatos: todos los colaboradores tienen un curso en proceso."
            columns={[{ key: 'nombre', label: 'Colaborador' }, { key: 'promedio', label: 'Promedio Score', render: r => pct(r.promedio) }, { key: 'activos', label: 'Cursos Activos' }]} />
        </VisualCard>
        <VisualCard title="Calificación" className="xl:col-span-6">
          <div className="overflow-auto max-h-[280px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#082234] z-10">
                <tr>
                  <th className="text-left py-2 px-2 font-medium text-white/40 uppercase text-[9px]">Rango</th>
                  <th className="text-left py-2 px-2 font-medium text-white/40 uppercase text-[9px]">Score</th>
                  <th className="text-left py-2 px-2 font-medium text-white/40 uppercase text-[9px]">Descripción</th>
                </tr>
              </thead>
              <tbody>
                {CALIFICACION_LEGEND.map(row => (
                  <tr key={row.rango} className="border-b border-white/5">
                    <td className="py-2 px-2 text-white/65 whitespace-nowrap">{row.rango}</td>
                    <td className="py-2 px-2">{starsFor(row.filled / 5)}</td>
                    <td className="py-2 px-2 text-white/55">{row.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </VisualCard>
        <VisualCard title="Score Colaborador" selected={cross.colaborador} onReset={() => setC('colaborador', cross.colaborador)} className="xl:col-span-12">
          <MiniTable keyField="correo" selected={cross.colaborador} onSelect={v => setC('colaborador', v)} rows={ranking}
            columns={[
              { key: 'nombre', label: 'Colaborador' },
              { key: 'promedio', label: 'Promedio Score', render: r => pct(r.promedio) },
              { key: 'estrellas', label: 'Score Colaborador', render: r => starsFor(r.promedio) },
            ]} />
        </VisualCard>
      </div>
    </div>
  );
}

function ExamenesDashboard({ rows, loading }) {
  const [global, setGlobal] = useState({ mes: '', correo: '', equipo: '', anio: '' });
  const [cross, setCross] = useState({ fabricante: '', resultado: '', tipo: '', reconocimiento: '', examen: '', colaborador: '' });
  const setC = (key, value) => setCross(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }));
  const reset = () => { setGlobal({ mes: '', correo: '', equipo: '', anio: '' }); setCross({ fabricante: '', resultado: '', tipo: '', reconocimiento: '', examen: '', colaborador: '' }); };
  const mes = r => norm(r.fecha_examen).slice(0, 7);
  const anio = r => norm(r.fecha_examen).slice(0, 4);
  const filtered = useMemo(() => rows.filter(r =>
    (!global.mes || mes(r) === global.mes)
    && (!global.correo || r.correo === global.correo)
    && (!global.equipo || r.equipo === global.equipo)
    && (!global.anio || anio(r) === global.anio)
    && (!cross.fabricante || r.fabricante === cross.fabricante)
    && (!cross.resultado || r.resultado === cross.resultado)
    && (!cross.tipo || r.tipo === cross.tipo)
    && (!cross.reconocimiento || r.tipo_reconocimiento === cross.reconocimiento)
    && (!cross.examen || r.examen_nombre === cross.examen)
    && (!cross.colaborador || r.correo === cross.colaborador)
  ), [rows, global, cross]);

  const personas = useMemo(() => {
    const map = new Map();
    filtered.forEach(r => {
      const item = map.get(r.correo) || { correo: r.correo, nombre: r.colaborador_nombre, equipo: r.equipo, puesto: r.colaborador_puesto, total: 0 };
      item.total += 1; map.set(r.correo, item);
    });
    return [...map.values()].sort((a, b) => b.total - a.total).slice(0, 30);
  }, [filtered]);
  const examenes = countBy(filtered, 'examen_nombre').slice(0, 15);
  const hoy = new Date().toISOString().slice(0, 10);
  const proximos = filtered.filter(r => r.fecha_examen && r.fecha_examen >= hoy).sort((a, b) => a.fecha_examen.localeCompare(b.fecha_examen)).slice(0, 20);
  const vencen = filtered.filter(r => r.activo_efectivo && r.fecha_expiracion && r.fecha_expiracion >= hoy).sort((a, b) => a.fecha_expiracion.localeCompare(b.fecha_expiracion)).slice(0, 20);
  const recientes = filtered.filter(r => r.fecha_examen).sort((a, b) => b.fecha_examen.localeCompare(a.fecha_examen)).slice(0, 25);
  const activos = Object.values(global).some(Boolean) || Object.values(cross).some(Boolean);

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <ResetBar filtrosActivos={activos} onReset={reset} />
      <GlobalFilters
        fields={[{ key: 'mes', all: 'Todos los meses' }, { key: 'correo', all: 'Todos los colaboradores' }, { key: 'equipo', all: 'Todos los equipos' }, { key: 'anio', all: 'Todos los años' }]}
        values={global} onChange={(k, v) => setGlobal(p => ({ ...p, [k]: v }))}
        options={{
          mes: unique(rows.map(r => ({ mes: mes(r) })), 'mes').reverse(),
          correo: [...new Map(rows.map(r => [r.correo, { value: r.correo, label: r.colaborador_nombre }])).values()].sort((a, b) => (a.label || '').localeCompare(b.label || '')),
          equipo: unique(rows, 'equipo'), anio: unique(rows.map(r => ({ anio: anio(r) })), 'anio').reverse(),
        }} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Exámenes" value={filtered.length} icon={BarChart3} />
        <Kpi label="Aprobados" value={filtered.filter(r => r.resultado === 'Aprobado').length} tone="#4ade80" icon={CheckCircle2} />
        <Kpi label="No aprobados" value={filtered.filter(r => r.resultado === 'No Aprobado').length} tone="#f87171" />
        <Kpi label="Pendientes" value={filtered.filter(r => r.resultado === 'Pendiente').length} tone="#f59e0b" />
        <Kpi label="Activos" value={filtered.filter(r => r.activo_efectivo).length} tone={ACCENT} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <VisualCard title="Exámenes por fabricante" selected={cross.fabricante} onReset={() => setC('fabricante', cross.fabricante)} className="xl:col-span-5">
          <Bars data={countBy(filtered, 'fabricante')} selected={cross.fabricante} onSelect={v => setC('fabricante', v)} colorFor={(e, i) => colorForFabricante(e.name, i)} />
        </VisualCard>
        <VisualCard title="Resultado" selected={cross.resultado} onReset={() => setC('resultado', cross.resultado)} className="xl:col-span-3">
          <Donut data={countBy(filtered, 'resultado')} selected={cross.resultado} onSelect={v => setC('resultado', v)} />
        </VisualCard>
        <VisualCard title="Tipo de examen" selected={cross.tipo} onReset={() => setC('tipo', cross.tipo)} className="xl:col-span-4">
          <Donut data={countBy(filtered, 'tipo')} selected={cross.tipo} onSelect={v => setC('tipo', v)} />
        </VisualCard>
        <VisualCard title="Exámenes de certificación" selected={cross.examen} onReset={() => setC('examen', cross.examen)} className="xl:col-span-7">
          <MiniTable keyField="name" selected={cross.examen} onSelect={v => setC('examen', v)} rows={examenes}
            columns={[{ key: 'name', label: 'Examen' }, { key: 'value', label: 'Cantidad' }]} />
        </VisualCard>
        <VisualCard title="Tipo de reconocimiento" selected={cross.reconocimiento} onReset={() => setC('reconocimiento', cross.reconocimiento)} className="xl:col-span-5">
          <Donut data={countBy(filtered, 'tipo_reconocimiento')} selected={cross.reconocimiento} onSelect={v => setC('reconocimiento', v)} />
        </VisualCard>
        <VisualCard title="Exámenes por colaborador" selected={cross.colaborador} onReset={() => setC('colaborador', cross.colaborador)} className="xl:col-span-7">
          <MiniTable keyField="correo" selected={cross.colaborador} onSelect={v => setC('colaborador', v)} rows={personas}
            columns={[{ key: 'nombre', label: 'Colaborador' }, { key: 'puesto', label: 'Puesto' }, { key: 'equipo', label: 'Equipo' }, { key: 'total', label: 'Exámenes' }]} />
        </VisualCard>
        <VisualCard title="Estatus de exámenes" selected={cross.colaborador} onReset={() => setC('colaborador', cross.colaborador)} className="xl:col-span-5">
          <MiniTable keyField="correo" selected={cross.colaborador} onSelect={v => setC('colaborador', v)} rows={recientes}
            columns={[{ key: 'colaborador_nombre', label: 'Colaborador' }, { key: 'examen_nombre', label: 'Examen' }, { key: 'fecha_examen', label: 'Fecha' }, { key: 'resultado', label: 'Resultado' }]} />
        </VisualCard>
        <VisualCard title="Próximos exámenes" selected={cross.examen} onReset={() => setC('examen', cross.examen)} className="xl:col-span-6">
          <MiniTable keyField="examen_nombre" selected={cross.examen} onSelect={v => setC('examen', v)} rows={proximos}
            columns={[{ key: 'fecha_examen', label: 'Fecha' }, { key: 'examen_nombre', label: 'Examen' }, { key: 'voucher_entregado_por', label: 'Voucher' }]} />
        </VisualCard>
        <VisualCard title="Certificaciones a vencer" selected={cross.colaborador} onReset={() => setC('colaborador', cross.colaborador)} className="xl:col-span-6">
          <MiniTable keyField="correo" selected={cross.colaborador} onSelect={v => setC('colaborador', v)} rows={vencen}
            columns={[{ key: 'colaborador_nombre', label: 'Colaborador' }, { key: 'examen_nombre', label: 'Certificación' }, { key: 'fecha_expiracion', label: 'Expira' }]} />
        </VisualCard>
      </div>
    </div>
  );
}

function Loading() {
  return <div className={`${CARD} h-64 flex items-center justify-center text-sm text-white/40`}><RefreshCw size={16} className="animate-spin mr-2" /> Cargando dashboard…</div>;
}

export default function CertificacionesDashboard() {
  const [tab, setTab] = useState('cursos');
  const [cursos, setCursos] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      certificacionesApi.listCursos({ limit: 1000 }),
      certificacionesApi.listExamenes({ limit: 1000 }),
    ]).then(([c, e]) => {
      setCursos(c.data || []); setExamenes(e.data || []);
    }).catch(err => setError(apiErrorMessage(err, 'No se pudo cargar el dashboard')))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-lg border border-sky-400/20 bg-[#061a2b]/70 p-1">
          {[{ id: 'cursos', label: 'Cursos' }, { id: 'examenes', label: 'Exámenes' }].map(item => (
            <button key={item.id} onClick={() => setTab(item.id)} className="px-4 py-2 rounded-md text-sm font-semibold"
              style={{ background: tab === item.id ? tint(.2) : 'transparent', color: tab === item.id ? ACCENT : 'rgba(255,255,255,.5)' }}>
              {item.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-white/35">{tab === 'cursos' ? cursos.length : examenes.length} registros</span>
      </div>
      {error ? <div className={`${CARD} p-5 text-red-300 text-sm`}>{error}</div> : tab === 'cursos'
        ? <CursosDashboard rows={cursos} loading={loading} />
        : <ExamenesDashboard rows={examenes} loading={loading} />}
    </div>
  );
}
